<template>
  <div class="leaderboard-wrap" id="leaderboard">
    <div class="titled-list">
      <div class="title">Leaderboard</div>

      <div class="list-wrapper">
        <div class="legend">
          <span class="pos">Pos</span><span class="name">Name</span
          ><span class="pts">Pts</span>
        </div>

        <div class="list" v-if="!loaded">
          <ol>
            <li v-for="(player, index) in mockTop8" :key="index">
              <span class="pos">{{ index + 1 }}.</span
              ><span class="name">{{ player.name }}</span
              ><span class="pts">{{ player.points }}</span>
            </li>
          </ol>
        </div>

        <div class="list">
          <transition-group name="flip-list" tag="ol">
            <popper
              trigger="hover"
              enter-active-class="animated fadeIn"
              :options="{
                placement: popoverPlacement(),
                modifiers: { offset: { offset: '0,15px' } }
              }"
              v-for="(player, index) in isInTop() || !currentUser
                ? top8
                : top8.slice(0, 7)"
              :key="player.id"
            >
              <LeaderboardPopover
                :player="player"
                :isSelf="currentUser && currentUser.uid === player.id"
                :userCanEditName="userCanEditName"
                @openShareModal="openShareModal()"
                @openEditNameModal="openEditNameModal()"
              />
              <li
                slot="reference"
                :class="{
                  'is-current-user':
                    currentUser && currentUser.uid === player.id
                }"
              >
                <span class="pos">{{ index + 1 }}.</span
                ><span class="name">{{ player.name }}</span
                ><span class="pts">{{ player.points }}</span>
              </li>
            </popper>
          </transition-group>
          <template v-if="!isInTop() && loaded && currentUser">
            <li :key="'seperator'" class="seperator"></li>

            <popper
              trigger="hover"
              enter-active-class="animated fadeIn"
              :options="{
                placement: popoverPlacement(),
                modifiers: { offset: { offset: '0,15px' } }
              }"
              :key="currentUser.uid + 'notInTop'"
            >
              <LeaderboardPopover
                :player="currentUser"
                :userCanEditName="userCanEditName"
                :isSelf="true"
                @openShareModal="openShareModal()"
                @openEditNameModal="openEditNameModal()"
              />
              <li slot="reference" class="is-current-user">
                <span class="pos">>></span
                ><span class="name">{{ currentUser.displayName }}</span
                ><span class="pts">{{ totalScore }}</span>
              </li>
            </popper>
          </template>
        </div>
      </div>
    </div>
    <div class="edit-name-wrapper" v-if="userCanEditName">
      <p style="text-align:center;margin-top: 20px">
        <a class="img-a-button" @click="openEditNameModal()">
          <img src="@/assets/btn-edit-name.png" alt="Open edit name dialog" />
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import { db } from "@/main.js";
import Popper from "vue-popperjs";
import "vue-popperjs/dist/vue-popper.css";
import LeaderboardPopover from "@/components/shared/LeaderboardPopover";
import { screenWidthUpTo767, isTempUser } from "../utils";

export default {
  components: { popper: Popper, LeaderboardPopover },
  props: ["currentUser", "totalScore", "bonusPoints", "baseScore"],
  data() {
    return {
      top8: [],
      mockTop8: [],
      loaded: false
    };
  },
  computed: {
    userCanEditName() {
      return isTempUser(this.currentUser);
    }
  },
  methods: {
    openEditNameModal() {
      this.$modal.show("edit-name-modal", {
        currentUser: this.currentUser
      });
    },
    openShareModal() {
      this.$modal.show("share-modal", {
        currentUser: this.currentUser,
        totalScore: this.totalScore
      });
    },
    popoverPlacement() {
      return screenWidthUpTo767() ? "top" : "right";
    },
    feedFakeData() {
      this.mockTop8 = Array.from({ length: 7 }, i => ({
        name: "Loading...",
        points: "--",
        id: i
      }));
    },
    async fetchTopPlayers() {
      db.collection("Users")
        .orderBy("totalScore", "desc")
        .limit(8)
        .onSnapshot(snap => {
          const { docs } = snap;
          this.top8 = docs
            .map(d => ({ data: d.data(), id: d.id }))
            .map(p => ({
              name: p.data.displayName,
              points: p.data.totalScore,
              bonusPoints: p.data.bonusPoints,
              baseScore: p.data.baseScore,
              id: p.id
            }))
            .slice(0, 8);
          this.loaded = true;
        });
    },
    isInTop() {
      return (
        this.currentUser &&
        this.top8.find(({ id }) => this.currentUser.uid === id)
      );
    }
  },
  beforeMount() {
    this.feedFakeData();
    this.fetchTopPlayers();
  }
};
</script>

<style lang="scss">
.flip-list-move {
  transition: transform 1s;
}

.leaderboard-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.titled-list {
  display: inline-block;
  min-width: 350px;
  .title {
    text-align: center;
    text-transform: uppercase;
  }
  .list-wrapper {
    @include pixelated-border();
    font-size: 0.625rem;

    .list li,
    .legend {
      margin: 5px;
      display: flex;
      justify-content: space-between;
      span {
        margin: 0 10px;
      }
    }

    .legend {
      background-color: $li-legend-bg-color;
      color: $li-legend-color;
    }

    .list {
      ol {
        list-style: none;
      }

      li {
        background-color: $li-bg-color;
        &:hover {
          background-color: $li-bg-hover-color;
          span.pos {
            color: $li-bg-color;
          }
        }

        span.pos {
          color: $li-accent-color;
        }
        span.name {
          flex: 2 0 auto;
        }
      }

      li.is-current-user {
        background-color: $li-accent-color;
        &:hover {
          background-color: $li-bg-hover-color;
        }

        span.pos {
          color: $li-bg-color;
        }
      }

      li.seperator {
        height: 4px;
        background-color: #683ad2;
      }
    }
  }
}

@media (max-width: 768px) {
  .popover {
    &::before {
      content: "";
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #231b4d;
      position: absolute;
      left: calc(45%);
      top: calc(100%);
    }
  }

  .titled-list {
    min-width: 300px;
  }
}

.animated.fadeIn {
  opacity: 0;
  animation: fade 300ms ease-out;
}

.animated.fadeOut {
  animation: fade 150ms ease-out reverse;
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>